import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, LogOut, Search, ShieldOff, CheckSquare, Handshake, Megaphone } from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BookOpen, Briefcase, FileText, Flag, Image, Inbox, LayoutDashboard, Menu, MessageCircle, Newspaper, ScrollText, Settings, Terminal, Users, Wallet, Receipt, Activity } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth, can } from "@/context/AuthContext";
import { roleLabel } from "@/lib/i18n";
import { EmptyState, Logo, ThemeToggle } from "@/components/common";
import { api } from "@/lib/api";

const ENV_LABEL = { development: "Développement", staging: "Préproduction", production: "Production" };

function EnvBadge() {
  const [h, setH] = useState(null);
  useEffect(() => {
    api.get("/health").then(({ data }) => setH(data)).catch(() => {});
  }, []);
  if (!h || (h.env === "production" && !h.simulation)) return null;
  return <span data-testid="console-env-badge" className="hidden xl:inline text-[11px] px-2 py-1 border border-mr-border rounded-full text-mr-muted">{ENV_LABEL[h.env] || h.env}{h.simulation ? " · simulation" : ""}</span>;
}

const NAV = [
  { section: "Pilotage" },
  { to: "/console", label: "Tableau de bord", icon: LayoutDashboard, perm: "console:access", end: true, id: "dashboard" },
  { to: "/console/engagement", label: "Statistiques", icon: Activity, perm: "members:read", id: "engagement" },
  { section: "Membres" },
  { to: "/console/membres", label: "Membres", icon: Users, perm: "members:read", id: "membres" },
  { to: "/console/admission", label: "Admissions et prospects", icon: Inbox, perm: "members:admit", id: "admission" },
  { to: "/console/demandes", label: "Demandes d'invitation", icon: Inbox, perm: "members:read", id: "demandes" },
  { to: "/console/parrainage", label: "Parrainages", icon: Users, perm: "members:read", id: "parrainage" },
  { section: "Validations" },
  { to: "/console/validations", label: "À valider", icon: CheckSquare, perm: "operations:manage", id: "validations" },
  { section: "Activité" },
  { to: "/console/operations", label: "Opérations", icon: Briefcase, perm: "operations:manage", id: "operations" },
  { to: "/console/evenements", label: "Événements et billets", icon: Briefcase, perm: "console:access", id: "evenements" },
  { to: "/console/fournisseurs", label: "Fournisseurs", icon: Users, perm: "moderation:manage", id: "fournisseurs" },
  { to: "/console/conventions", label: "Modèles de convention", icon: FileText, perm: "cms:manage", id: "conventions" },
  { section: "Finances" },
  { to: "/console/cotisations", label: "Cotisations et paiements", icon: Receipt, perm: "payments:read", id: "cotisations" },
  { to: "/console/finances", label: "Finances", icon: Wallet, perm: "finances:read", id: "finances" },
  { section: "Communauté" },
  { to: "/console/annonces", label: "Annonces du club", icon: Megaphone, perm: "moderation:manage", id: "annonces" },
  { to: "/console/moderation", label: "Fil, signalements et modération", icon: Flag, perm: "moderation:manage", id: "moderation" },
  { to: "/console/messagerie", label: "Groupes de discussion et notifications", icon: MessageCircle, perm: "cms:manage", id: "messagerie" },
  { section: "Contenu" },
  { to: "/console/contenu", label: "Pages du site et FAQ", icon: FileText, perm: "cms:manage", id: "contenu" },
  { to: "/console/blog", label: "Blog et guides", icon: Newspaper, perm: "cms:manage", id: "blog" },
  { to: "/console/partenaires", label: "Partenaires", icon: Handshake, perm: "cms:manage", id: "partenaires" },
  { to: "/console/mediatheque", label: "Médias", icon: Image, perm: "cms:manage", id: "mediatheque" },
  { section: "Administration" },
  { to: "/console/parametres", label: "Paramètres", icon: Settings, perm: "settings:read", id: "parametres" },
  { to: "/console/audit", label: "Journal d'audit", icon: ScrollText, perm: "audit:read", id: "audit" },
  { to: "/console/journal-dev", label: "Journal dev", icon: Terminal, perm: "devlog:read", id: "journal-dev" },
  { to: "/console/documentation", label: "Documentation technique", icon: BookOpen, perm: "docs:read", id: "documentation" },
];

function GlobalSearch() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    const k = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { api.get("/admin/search", { params: { q } }).then(({ data }) => setItems(data)).catch(() => {}); }, 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <>
      <button data-testid="console-search-button" onClick={() => setOpen(true)} className="mr-btn-ghost min-h-[40px] gap-2"><Search size={16} /><span className="hidden xl:inline text-mr-muted text-xs">Rechercher · Ctrl K</span></button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput data-testid="console-search-input" placeholder="Membre, opération, paiement, article…" value={q} onValueChange={setQ} />
        <CommandList><CommandEmpty>{q.length < 2 ? "Tapez au moins 2 caractères." : "Aucun résultat."}</CommandEmpty>
          <CommandGroup>{items.map((r, i) => (
            <CommandItem key={i} value={`${r.type} ${r.title} ${i}`} data-testid="console-search-result" onSelect={() => { setOpen(false); nav(r.link); }}>
              <span className="text-xs text-mr-accent w-20 shrink-0">{r.type}</span><span className="truncate">{r.title}</span><span className="ml-auto text-xs text-mr-muted">{r.sub}</span></CommandItem>))}</CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}

function BellMenu() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const load = () => api.get("/notifications").then(({ data }) => setItems(Array.isArray(data) ? data : data.items || [])).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  const unread = items.filter((n) => !n.read_at && !n.read).length;
  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger data-testid="console-bell" aria-label="Notifications" className="relative grid place-items-center w-11 h-11 border border-mr-border rounded-[3px]">
        <Bell size={17} />{unread > 0 && <span data-testid="console-bell-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-mr-accent text-mr-on-accent text-[10px] font-bold grid place-items-center">{unread > 9 ? "9+" : unread}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        {items.length === 0 ? <p className="p-3 text-sm text-mr-muted">Aucune notification.</p> : items.slice(0, 20).map((n) => (
          <DropdownMenuItem key={n.id} data-testid="console-notification" onClick={() => { api.post("/notifications/read-all").catch(() => {}); if (n.link) nav(n.link); }} className="flex-col items-start">
            <span className={`text-sm ${n.read_at || n.read ? "" : "font-semibold"}`}>{n.title}</span><span className="text-xs text-mr-muted line-clamp-2">{n.body}</span></DropdownMenuItem>))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="console-user-menu" className="mr-btn-ghost min-h-[40px]">{me.user.name.split(" ")[0]}</DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <p className="px-2 py-1 text-xs text-mr-muted">{roleLabel(me.user.global_role)}</p>
        <DropdownMenuItem data-testid="console-logout" onClick={async () => { await logout(); nav("/console/connexion"); }}><LogOut size={14} className="mr-2" /> Se déconnecter</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SideNav({ onClick }) {
  const { me } = useAuth();
  const items = NAV.filter((n) => n.section || can(me, n.perm)).filter((n, i, arr) => !n.section || (arr[i + 1] && !arr[i + 1].section));
  return (
    <nav className="flex flex-col gap-0.5">
      {items.map(({ to, label, icon: Icon, end, id, section }) => section ? <p key={section} className="mr-eyebrow px-3 pt-4 pb-1">{section}</p> : (
        <NavLink key={to} to={to} end={end} onClick={onClick} data-testid={`console-nav-${id}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 min-h-[44px] rounded-[3px] text-sm transition-colors ${isActive ? "bg-mr-raised text-mr-text border-l-2 border-mr-accent" : "text-mr-text2 hover:text-mr-text hover:bg-mr-surface"}`}>
          <Icon size={17} strokeWidth={1.7} /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function ConsoleLayout() {
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const route = [...NAV].filter((n) => n.to && n.to !== "/console").find((n) => pathname === n.to || pathname.startsWith(`${n.to}/`)) || NAV.find((n) => n.to === pathname.replace(/\/$/, ""));
  const allowed = !route || can(me, route.perm);
  return (
    <div className="min-h-screen bg-mr-bg flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-mr-border p-4 gap-6 sticky top-0 h-screen overflow-y-auto">
        <div className="px-2 pt-1"><Logo to="/console" testId="console-logo-link" /><p className="text-xs text-mr-muted mt-1">Console</p></div>
        <SideNav />
      </aside>
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 h-14 border-b border-mr-border bg-mr-bg flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-2 lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><button data-testid="console-mobile-menu-button" aria-label="Menu" className="grid place-items-center w-11 h-11 border border-mr-border rounded-[3px]"><Menu size={18} /></button></SheetTrigger>
              <SheetContent side="left" className="bg-mr-bg border-mr-border w-72">
                <SheetTitle className="sr-only">Menu console</SheetTitle>
                <div className="mt-8"><SideNav onClick={() => setOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            <Logo to="/console" testId="console-mobile-logo" compact />
          </div>
          <span className="hidden lg:block text-sm text-mr-muted" data-testid="console-role-label">{me.user.name} · {roleLabel(me.user.global_role)}</span>
          <div className="flex items-center gap-2">
            <GlobalSearch />
            <EnvBadge />
            <Link to="/app" data-testid="console-member-space-link" className="mr-btn-ghost hidden xl:inline-flex">Espace membre</Link>
            <BellMenu />
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-6xl">
          {allowed ? <Outlet /> : <EmptyState icon={ShieldOff} title="Accès refusé" text="Votre rôle ne donne pas accès à cette section de la console." testId="console-access-denied" />}
        </main>
      </div>
    </div>
  );
}
